import { useEffect, useState } from "react";
import { PageHeader } from "@/components/PageHeader";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Trash2 } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";

export default function EmailTemplates() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [rows, setRows] = useState([]);
  const [selected, setSelected] = useState(null);
  const [checked, setChecked] = useState(new Set());
  const [bulkDeleteOpen, setBulkDeleteOpen] = useState(false);
  const [bulkDeleting, setBulkDeleting] = useState(false);

  const load = async () => {
    const r = await api.get("/email-templates");
    setRows(r.data);
    if (r.data.length && !selected) setSelected(r.data[0]);
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, []);

  const save = async () => {
    try {
      await api.post("/email-templates", {
        key: selected.key, name: selected.name, subject: selected.subject, body: selected.body,
      });
      toast.success("Template saved");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  };

  const toggleOne = (key) => setChecked((s) => {
    const n = new Set(s); n.has(key) ? n.delete(key) : n.add(key); return n;
  });
  const toggleAllOnPage = () => setChecked((s) => {
    const allKeys = rows.map((t) => t.key);
    const allSelected = allKeys.every((k) => s.has(k));
    if (allSelected) { const n = new Set(s); allKeys.forEach((k) => n.delete(k)); return n; }
    const n = new Set(s); allKeys.forEach((k) => n.add(k)); return n;
  });
  const clearSelection = () => setChecked(new Set());
  const bulkDelete = async () => {
    setBulkDeleting(true);
    try {
      const r = await api.post("/email-templates/bulk-delete", { ids: Array.from(checked) });
      toast.success(`Deleted ${r.data.deleted} template${r.data.deleted === 1 ? "" : "s"}`);
      // If the currently-selected template was deleted, clear it
      if (selected && checked.has(selected.key)) setSelected(null);
      clearSelection();
      setBulkDeleteOpen(false);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Bulk delete failed");
    } finally { setBulkDeleting(false); }
  };

  return (
    <div>
      <PageHeader testid="page-email-templates" title="Email templates" subtitle="Placeholders: {client_name} · {job_title} · {contact_name} · {deadline_uk}" />
      <div className="p-6 lg:p-8 grid grid-cols-1 lg:grid-cols-4 gap-6">
        <div className="border border-slate-200 rounded-md bg-white overflow-hidden lg:col-span-1">
          <div className="px-4 py-3 border-b border-slate-200 text-[11px] uppercase tracking-[0.14em] font-semibold text-slate-500 flex items-center justify-between gap-2">
            <div className="flex items-center gap-2">
              {isAdmin && (
                <Checkbox
                  data-testid="templates-select-all"
                  checked={rows.length > 0 && rows.every((t) => checked.has(t.key))}
                  onCheckedChange={toggleAllOnPage}
                  aria-label="Select all templates"
                />
              )}
              <span>Templates</span>
            </div>
            {isAdmin && (
              <button
                data-testid="new-template-btn"
                onClick={() => setSelected({ key: "custom_" + Date.now(), name: "New template", subject: "", body: "" })}
                className="text-slate-600 hover:text-slate-900 text-xs normal-case tracking-normal font-normal"
              >
                + New
              </button>
            )}
          </div>
          {checked.size > 0 && (
            <div data-testid="templates-bulk-bar" className="bg-slate-900 text-slate-100 px-3 py-2 flex items-center justify-between text-xs">
              <span data-testid="templates-selected-count">
                <span className="font-semibold">{checked.size}</span> selected
              </span>
              <div className="flex items-center gap-2">
                <button type="button" onClick={clearSelection} className="text-slate-300 hover:text-white" data-testid="templates-clear-selection">Clear</button>
                <Button variant="destructive" size="sm" onClick={() => setBulkDeleteOpen(true)} className="h-7 text-xs" data-testid="templates-bulk-delete-btn">
                  <Trash2 className="h-3 w-3 mr-1" /> Delete
                </Button>
              </div>
            </div>
          )}
          <ul>
            {rows.map((t) => (
              <li key={t.key} className={`flex items-stretch border-b border-slate-100 ${checked.has(t.key) ? "bg-slate-50" : ""}`}>
                {isAdmin && (
                  <div className="flex items-center px-3 border-r border-slate-100">
                    <Checkbox
                      data-testid={`template-select-${t.key}`}
                      checked={checked.has(t.key)}
                      onCheckedChange={() => toggleOne(t.key)}
                      aria-label={`Select ${t.name}`}
                    />
                  </div>
                )}
                <button
                  onClick={() => setSelected(t)}
                  className={`flex-1 text-left px-3 py-3 text-sm hover:bg-slate-50 transition-colors ${selected?.key === t.key ? "bg-slate-50" : ""}`}
                >
                  <div className="font-medium text-slate-900">{t.name}</div>
                  <div className="text-xs text-slate-500 font-mono truncate">{t.key}</div>
                </button>
              </li>
            ))}
          </ul>
        </div>

        <div className="lg:col-span-3 border border-slate-200 rounded-md bg-white p-5 space-y-4">
          {!selected && <div className="text-sm text-slate-500 text-center py-16">Pick a template.</div>}
          {selected && (
            <>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Key (identifier)</Label>
                  <Input value={selected.key} onChange={(e) => setSelected({ ...selected, key: e.target.value })} disabled={!isAdmin} />
                </div>
                <div>
                  <Label>Name</Label>
                  <Input data-testid="template-name-input" value={selected.name} onChange={(e) => setSelected({ ...selected, name: e.target.value })} disabled={!isAdmin} />
                </div>
              </div>
              <div>
                <Label>Subject</Label>
                <Input data-testid="template-subject-input" value={selected.subject} onChange={(e) => setSelected({ ...selected, subject: e.target.value })} disabled={!isAdmin} />
              </div>
              <div>
                <Label>Body</Label>
                <Textarea data-testid="template-body-input" rows={10} value={selected.body} onChange={(e) => setSelected({ ...selected, body: e.target.value })} disabled={!isAdmin} />
              </div>
              {isAdmin ? (
                <div className="flex justify-end">
                  <Button data-testid="save-template-btn" onClick={save} className="rounded-md bg-slate-900 hover:bg-slate-800">Save template</Button>
                </div>
              ) : (
                <div className="text-xs text-slate-500">Only admins can edit templates.</div>
              )}
            </>
          )}
        </div>
      </div>

      <AlertDialog open={bulkDeleteOpen} onOpenChange={setBulkDeleteOpen}>
        <AlertDialogContent data-testid="templates-bulk-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete {checked.size} template{checked.size === 1 ? "" : "s"}?</AlertDialogTitle>
            <AlertDialogDescription>
              This action cannot be undone. Note: any built-in template (keys starting with
              <span className="font-mono"> chaser_</span>) will be re-seeded automatically the next time
              the backend restarts. Custom templates you&apos;ve created are gone for good.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={bulkDeleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={bulkDelete}
              disabled={bulkDeleting}
              data-testid="templates-bulk-delete-confirm"
              className="bg-rose-600 hover:bg-rose-700"
            >
              {bulkDeleting ? "Deleting…" : `Yes, delete ${checked.size}`}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
